<template>
  <div>
    <div v-if="(notes && notes.length > 0) || creatingNote" class="card">
      <div class="card-body card-padding">
        <div class="row">
          <div class="col-xs-12">
            <h4>
              {{ $t('Notes') }}
            </h4>
          </div>
        </div>
        <div v-if="notes && notes.length > 0" class="row notelist m-b-15">
          <NotesAppNote
            v-for="note in notes"
            ref="note"
            :key="note.id"
            :note="note"
            :new="note.new"
            :show-author="true"
            @loading="childLoading = $event"
            @edit="handleEditNote"
            @deleted="handleDeletedNote"
          />
        </div>
        <textarea
          v-if="creatingNote"
          ref="newNoteTextArea"
          v-model="noteValue"
          class="form-control valid f-14 p-0 tw-mb-2"
        />
        <div v-if="allowNoteCreation" class="row addnote">
          <div class="col-xs-12">
            <a
              v-if="creatingNote"
              :class="{
                skeleton: loading,
                disabled: loading || noteValue === '',
              }"
              href="#"
              class="btn btn-default btn-block waves-effect"
              @click="addNote"
            >
              {{ loading ? $t('Saving changes') : $t('Add note') }}
            </a>
            <a
              v-else-if="!childEditing"
              :class="{
                skeleton: loading,
                disabled: loading,
              }"
              href="#"
              class="btn btn-default btn-block waves-effect"
              @click="openNoteTextArea"
            >
              {{ loading ? $t('Saving changes') : $t('Add note') }}
            </a>
            <a
              v-else
              :class="{
                skeleton: childLoading,
                disabled: childLoading,
              }"
              href="#"
              class="btn btn-default btn-block waves-effect"
            >
              {{ $t('Save changes') }}
            </a>
          </div>
        </div>
        <span
          v-if="allowNoteCreation && notes && notes.length > 0"
          class="pull-right iblock c-gray small p-t-5"
        >
          {{ childLoading ? $t('Saving changes') : $t('All changes saved') }}
        </span>
      </div>
    </div>
    <div
      v-if="(!notes || notes.length < 1) && allowNoteCreation && !creatingNote"
      class="row addnote text-center"
    >
      <div
        :class="{
          skeleton: loading,
          disabled: loading,
        }"
        class="col-xs-12"
      >
        <a href="#" class="btn btn-default btn-block waves-effect" @click="openNoteTextArea">
          {{ $t('Add notes') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { nextTick } from 'vue'
import NotesAppNote from '@/components/NotesAppNote.vue'
import { deepClone } from '@/utils/tools'
import { NetworkError } from '@/utils/notification'
import enums from '@/utils/enum'

export default {
  name: 'NotesApp',
  components: { NotesAppNote },
  apollo: {
    notes: {
      client: 'publicApi',
      query: gql`
        query notes($jobId: ID!) {
          job(id: $jobId) {
            id
            notes(first: 100) {
              data {
                id
                title
                body
                viewerCanUpdate
                viewerCanDelete
                account {
                  ... on Company {
                    avatar
                  }
                }
                author {
                  id
                  name
                  avatar
                }
              }
            }
          }
        }
      `,
      update: (data) => deepClone(data.job.notes.data),
      variables() {
        return {
          jobId: this.jobId,
        }
      },
    },
  },

  props: {
    jobId: {
      type: String,
      required: true,
    },
    showNoteAuthors: {
      type: Boolean,
      default: false,
    },
    allowNoteCreation: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
  },
  data() {
    return {
      notes: null,
      childLoading: false,
      childEditing: false,
      loadingValue: false,
      creatingNote: false,
      noteValue: '',
    }
  },
  computed: {
    loading: {
      get() {
        return this.$apollo.loading || this.childLoading || this.loadingValue || false
      },
      set(val) {
        this.loadingValue = val
      },
    },
    parsedJobId() {
      return parseInt(this.jobId, 10)
    },
  },
  methods: {
    openNoteTextArea() {
      if (!this.creatingNote) {
        this.creatingNote = true

        nextTick(() => this.$refs.newNoteTextArea.focus())
      }
    },
    handleEditNote(event) {
      this.childEditing = event
      this.creatingNote = false
      this.noteValue = ''
    },
    async addNote() {
      if (!this.notes) {
        this.notes = []
      }
      this.loading = true
      try {
        const note = await this.$apollo.mutate({
          mutation: gql`
            mutation createNote($input: CreateNoteInput!) {
              createNote(input: $input) {
                id
                viewerCanUpdate
                viewerCanDelete
                body
                account {
                  name
                  id
                  ... on Company {
                    avatar
                  }
                }
                author {
                  name
                  avatar
                  id
                }
              }
            }
          `,
          client: 'publicApi',
          variables: {
            input: {
              notableId: this.jobId,
              accountId: currentAccount.globalId,
              body: this.noteValue,
            },
          },
        })
        this.loading = false
        this.creatingNote = false
        this.noteValue = ''
        this.notes.push(note.data.createNote)
      } catch (e) {
        this.loading = false
        new NetworkError({
          msg: this.$t('Was not able to create the note'),
          severity: enums.notification.severity.CRITICAL,
          ex: e,
        }).dispatch(this)
      }
    },
    handleDeletedNote(noteId) {
      const notesList = this.notes
      const index = notesList.map((e) => e.id).indexOf(noteId)
      if (index > -1) {
        notesList.splice(index, 1)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.disabled {
  pointer-events: none;
  cursor: default;
  background-color: transparent;
}
</style>
